/*
    🔥 DIÁLOGOS DE SWEETALERT — implementación canónica

    ⚠️ POR QUÉ ESTE ARCHIVO SE MUDÓ ACÁ

    Vivía en _HELPERS, y era la ÚNICA dependencia de Templates hacia
    afuera de sí misma. Con eso, copiar la carpeta Templates a otro
    proyecto rompía el confirm de sweetalert hasta que alguien se
    acordara de llevar también ese helper.

    Ahora la implementación vive dentro de Templates y
    _HELPERS/swal2_alert.helper.js la reexporta. Los 65 archivos del
    proyecto que la importaban siguen andando exactamente igual: no
    hay que tocar ni uno.

    ⚠️ SI ALGÚN DÍA BORRÁS EL REEXPORT

    Antes buscá "swal2_alert" en todo el proyecto. Son muchos archivos
    y la mayoría están en el baúl, pero cuatro vistas vivas del blog y
    tres hooks de _CORE también lo usan.
*/

import Swal from "sweetalert2";

/*
    🔥 CAMBIO: el fondo y el texto ahora salen de tus variables.

    Antes eran "#15110d" y "#f3ede4" fijos: un marrón oscuro que no
    seguía el tema y, como sweetalert los aplica INLINE, pisaba todo
    lo que ConfirmSweetAlert.css intentaba poner.

    Las CSS vars funcionan en estilos inline, así que con esto el swal
    cambia solo entre claro y oscuro.
*/
const BASE_CONFIG = {
    background: "var(--LS__app_priv_card_bg)",
    color: "var(--LS__app_priv_text_main)",
    customClass: {
        popup: "LStudio__app_container_custom_swal2_portal",
        title: "LStudio__app_content_custom_swal2_title",
        htmlContainer: "LStudio__app_content_custom_swal2_text",
        confirmButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_confirm",
        cancelButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_cancel",
        denyButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_danger",
        actions: "LStudio__app_content_custom_swal2_actions",
        icon: "LStudio__app_content_custom_swal2_icon",
    }, showClass: { popup: "swal2-show" }, hideClass: { popup: "swal2-hide" },
    buttonsStyling: false, reverseButtons: true, focusCancel: true,
};

/*
    🔥 TEXTOS POR DEFECTO — se inyectan desde LanguageProvider

    Este archivo NO es un componente: no puede usar hooks. El proveedor
    de idioma llama a onSetAlertTexts en cada cambio y acá quedan los
    textos activos. Si nadie lo llama, siguen los de ALERTS_FALLBACK.
*/
const ALERTS_FALLBACK = {
    confirmTitle: "¿Confirmar acción?", confirmOk: "Confirmar", cancel: "Cancelar",
    archiveTitle: "¿Archivar este registro?", archiveOk: "Sí, archivar",
    deleteTitle: "¿Eliminar definitivamente?", deleteOk: "Eliminar definitivamente",
    deleteInputLabel: "Para confirmar, escribí exactamente: ",
    deleteInputPlaceholder: "Escribí acá para habilitar el botón",
    bulkDeleteTitle: "Confirmá la eliminación masiva", bulkDeletePlaceholder: "Escribí ELIMINAR",
    bulkDeleteWord: "ELIMINAR", bulkDeleteHint: "Para confirmar, escribí:", errCode: "El código debe ser de 6 dígitos.",
    errWord: "Debés escribir ELIMINAR exactamente.", importTitle: "No se pudo importar",
    importMore: "…y {{count}} fila(s) más con errores.", successTitle: "¡Listo!",
    errorTitle: "Ocurrió un error", warningTitle: "Atención", infoTitle: "Información",
    loadingTitle: "Procesando...", ok: "OK", close: "Cerrar", understood: "Entendido",
};

let ALERTS = { ...ALERTS_FALLBACK };

export const onSetAlertTexts = (dict = null) => {
    ALERTS = { ...ALERTS_FALLBACK, ...(dict ?? {}) };
};

const _fire = (opts) => Swal.fire({ ...BASE_CONFIG, ...opts });

const _confirmBase = async (opts) => {
    const r = await _fire(opts);
    return r.isConfirmed;
};

export const SWAL2_ALERT_HELPER = {

    confirm: ({ title = ALERTS.confirmTitle, text, html, confirmText = ALERTS.confirmOk,
        cancelText = ALERTS.cancel } = {}) => _confirmBase({
            title, text, html, icon: "question",
            showCancelButton: true, confirmButtonText: confirmText,
            cancelButtonText: cancelText,
        }),

    confirmArchive: ({ title = ALERTS.archiveTitle, text, html, confirmText = ALERTS.archiveOk,
        cancelText = ALERTS.cancel, } = {}) => _confirmBase({
            title, text, html, icon: "warning",
            showCancelButton: true, confirmButtonText: confirmText,
            cancelButtonText: cancelText, customClass: {
                ...BASE_CONFIG.customClass,
                popup: "LStudio__app_container_custom_swal2_portal LStudio__app_container_custom_swal2_portal_content_archive",
                confirmButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_archive",
            },
        }),

    confirmHardDelete: async (OBJ = {}) => {

        const { expectedText, title = ALERTS.deleteTitle,
            text, html, confirmText = ALERTS.deleteOk,
            cancelText = ALERTS.cancel, inputLabel = ALERTS.deleteInputLabel } = OBJ;

        if (!expectedText) {
            console.warn("[SWAL2_ALERT_HELPER.confirmHardDelete] Falta `expectedText`.");
            return false;
        }

        const r = await _fire({
            title, html: `
                ${html ?? text ?? ""} <div class="LStudio__app_container_custom_swal2_confirm_input_wrap">
                    <label class="LStudio__app_content_custom_swal2_confirm_label">
                        ${inputLabel}<code class="LStudio__app_content_custom_swal2_confirm_token">${expectedText}</code>
                    </label>
                    <input  id="LStudio__app_content_custom_swal2_confirm_input"  class="LStudio__app_content_custom_swal2_confirm_input"
                        type="text"  autocomplete="off"    placeholder="${ALERTS.deleteInputPlaceholder}" />
                </div>
            `, icon: "error", showCancelButton: true, confirmButtonText: confirmText,
            cancelButtonText: cancelText, focusCancel: true,
            customClass: {
                ...BASE_CONFIG.customClass,
                popup: "LStudio__app_container_custom_swal2_portal LStudio__app_custom_swal2_portal_content--danger",
                confirmButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_hard_danger",
            },
            didOpen: () => {
                const confirmBtn = Swal.getConfirmButton();
                const input = document.getElementById("LStudio__app_content_custom_swal2_confirm_input");
                confirmBtn.disabled = true;
                confirmBtn.classList.add("is-disabled");

                input?.addEventListener("input", (e) => {
                    const matches = e.target.value.trim() === String(expectedText).trim();
                    confirmBtn.disabled = !matches;
                    confirmBtn.classList.toggle("is-disabled", !matches);
                });
            },
            preConfirm: () => {
                const input = document.getElementById("LStudio__app_content_custom_swal2_confirm_input");
                const value = (input?.value ?? "").trim();
                if (value !== String(expectedText).trim()) {
                    Swal.showValidationMessage("El texto no coincide.");
                    return false;
                }
                return true;
            },
        });

        return r.isConfirmed;
    },

    confirmBulkHardDelete: async (OBJ = {}) => {

        const { count, emailMasked, expiresIn = "10 minutos",
            title = ALERTS.bulkDeleteTitle, confirmText = ALERTS.deleteOk,
            cancelText = ALERTS.cancel } = OBJ;

        const r = await _fire({
            title, html: `
            <div class="LStudio__app_content_custom_swal2_bulk_info">
                Te enviamos un <strong>código de verificación</strong> a <code>${emailMasked}</code>.<br>
                Tenés <strong>${expiresIn}</strong> para confirmar la eliminación de <strong>${count}</strong> registro(s).
            </div>

            <div class="LStudio__app_container_custom_swal2_confirm_input_wrap">
                <label class="LStudio__app_content_custom_swal2_confirm_label">Código de 6 dígitos</label>
                <input id="LStudio__app_custom_swal2_bulk_code"
                    class="LStudio__app_content_custom_swal2_confirm_input"
                    type="text" inputmode="numeric"
                    maxlength="6" autocomplete="off"
                    placeholder="123456" />
            </div>

            <div class="LStudio__app_container_custom_swal2_confirm_input_wrap">
                <label class="LStudio__app_content_custom_swal2_confirm_label">
                    ${ALERTS.bulkDeleteHint} <code class="LStudio__app_content_custom_swal2_confirm_token">${ALERTS.bulkDeleteWord}</code>
                </label>
                <input id="LStudio__app_custom_swal2_bulk_word"
                    class="LStudio__app_content_custom_swal2_confirm_input"
                    type="text" autocomplete="off"
                    placeholder="${ALERTS.bulkDeletePlaceholder}" />
            </div>
        `,
            icon: "error", showCancelButton: true,
            confirmButtonText: confirmText, cancelButtonText: cancelText,
            focusCancel: true, customClass: {
                ...BASE_CONFIG.customClass,
                popup: "LStudio__app_container_custom_swal2_portal LStudio__app_custom_swal2_portal_content--danger",
                confirmButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_hard_danger",
            },
            didOpen: () => {
                const confirmBtn = Swal.getConfirmButton();
                const codeInput = document.getElementById("LStudio__app_custom_swal2_bulk_code");
                const wordInput = document.getElementById("LStudio__app_custom_swal2_bulk_word");

                confirmBtn.disabled = true;
                confirmBtn.classList.add("is-disabled");

                const validate = () => {
                    const codeOK = codeInput.value.trim().length === 6 && /^\d{6}$/.test(codeInput.value.trim());
                    const wordOK = wordInput.value.trim().toUpperCase() === ALERTS.bulkDeleteWord;
                    const allOK = codeOK && wordOK;
                    confirmBtn.disabled = !allOK;
                    confirmBtn.classList.toggle("is-disabled", !allOK);
                };

                codeInput?.addEventListener("input", validate);
                wordInput?.addEventListener("input", validate);

                codeInput?.focus();
            },
            preConfirm: () => {
                const code = document.getElementById("LStudio__app_custom_swal2_bulk_code")?.value.trim() ?? "";
                const word = document.getElementById("LStudio__app_custom_swal2_bulk_word")?.value.trim() ?? "";

                if (!/^\d{6}$/.test(code)) {
                    Swal.showValidationMessage(ALERTS.errCode);
                    return false;
                }
                if (word.toUpperCase() !== ALERTS.bulkDeleteWord) {
                    Swal.showValidationMessage(ALERTS.errWord);
                    return false;
                }

                return { code, confirmText: word };
            },
        });

        return r.isConfirmed ? r.value : null;
    },

    errorBulkImport: (OBJ = {}) => {

        const { title = ALERTS.importTitle, summary, errors = [], totalRows, maxVisible = 15, confirmText = ALERTS.close, } = OBJ;

        const visibleErrors = errors.slice(0, maxVisible);
        const remaining = errors.length - visibleErrors.length;

        const renderRowError = (err) => `
        <div class="LStudio__app_container_custom_swal2_import_row">
            <div class="LStudio__app_container_custom_swal2_import_row_head">
                <span class="LStudio__app_content_custom_swal2_import_line">Fila ${err.line ?? "—"}</span>
                <span class="LStudio__app_content_custom_swal2_import_name">${err.name ?? "—"}</span>
            </div>
            <ul class="LStudio__app_container_custom_swal2_import_errors">
                ${(err.errors ?? []).map(e => `
                    <li>
                        ${e.field ? `<code class="LStudio__app_container_custom_swal2_import_field">${e.field}</code>` : ""}
                        <span>${e.message}</span>
                    </li>
                `).join("")}
            </ul>
        </div>
    `;

        return _fire({
            title, html: `${summary ? `<p class="LStudio__app_content_custom_swal2_import_summary">${summary}</p>` : ""}

            <div class="LStudio__app_container_custom_swal2_import_list">
                ${visibleErrors.map(renderRowError).join("")}
                ${remaining > 0 ? `<p class="LStudio__app_content_custom_swal2_import_more">${ALERTS.importMore.replace("{{count}}", remaining)}</p>` : ""}
            </div>

            ${totalRows ? `
                <div class="LStudio__app_container_custom_swal2_import_footer">
                    <span>Total: <strong>${totalRows}</strong> fila(s)</span>
                    <span class="LStudio__app_content_custom_swal2_import_bullet">•</span>
                    <span class="LStudio__app_content_custom_swal2_import_failed"><strong>${errors.length}</strong> con error</span>
                </div>
            ` : ""}  `,
            icon: "error", confirmButtonText: confirmText,
            width: 620, customClass: {
                ...BASE_CONFIG.customClass,
                popup: "LStudio__app_container_custom_swal2_portal LStudio__app_container_custom_swal2_portal_content_bulk_import_error",
                confirmButton: "LStudio__app_content_custom_swal2_btn LStudio__app_content_custom_swal2_btn_confirm",
            },
        });
    },

    success: ({ title = ALERTS.successTitle, text, html, confirmText = ALERTS.ok } = {}) => _fire({ title, text, html, icon: "success", confirmButtonText: confirmText }),

    error: ({ title = ALERTS.errorTitle, text, html, confirmText = ALERTS.close } = {}) => _fire({ title, text, html, icon: "error", confirmButtonText: confirmText }),

    warning: ({ title = ALERTS.warningTitle, text, html, confirmText = ALERTS.understood } = {}) => _fire({ title, text, html, icon: "warning", confirmButtonText: confirmText }),

    info: ({ title = ALERTS.infoTitle, text, html, confirmText = ALERTS.ok } = {}) => _fire({ title, text, html, icon: "info", confirmButtonText: confirmText }),

    toast: ({ title, icon = "success", timer = 2500, position = "top-end" } = {}) => Swal.fire({
        toast: true, position, icon, title, timer, timerProgressBar: true,
        showConfirmButton: false,
        background: "var(--LS__app_priv_surface)",
        color: "var(--LS__app_priv_text_main)",
        customClass: { popup: "LStudio__app_content_custom_swal2_toast" },
    }),

    loading: (title = ALERTS.loadingTitle) => _fire({
        title, allowOutsideClick: false, allowEscapeKey: false,
        showConfirmButton: false, didOpen: () => Swal.showLoading(),
    }),

    close: () => Swal.close(),
};